import React from "react";
import { ScheduleComparison, ComparisonMetrics, milesFromMeters, hoursFromSeconds } from "../../../models/ScheduleComparison";
import "./Scorecard.scss";

type Row =
  | { kind: "section"; label: string }
  | { kind: "row"; label: string; get: (m: ComparisonMetrics) => string; indent?: boolean; subtotal?: boolean };

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

// Maintenance leads the not-billed group: it's a notional per-mile wear estimate,
// not a real cash outlay, so it's grouped with the soft penalties rather than counted
// as operating spend.
const penaltyDefs: { label: string; pick: (m: ComparisonMetrics) => number }[] = [
  { label: "Maintenance", pick: (m) => m.cost.maintenance },
  { label: "Compactness", pick: (m) => m.cost.compactness },
  { label: "Overtime", pick: (m) => m.cost.overtime },
  { label: "Time-window", pick: (m) => m.cost.timeWindow },
  { label: "End-time", pick: (m) => m.cost.endTime },
  { label: "Balance", pick: (m) => m.cost.balance },
];

export default function ComparisonTable({ comparison }: { comparison: ScheduleComparison }) {
  const { benchmark, actual } = comparison;

  const shownPenalties = penaltyDefs.filter(
    (p) => (Number(p.pick(benchmark)) || 0) !== 0 || (Number(p.pick(actual)) || 0) !== 0
  );
  const hasPenalties = shownPenalties.length > 0;

  const rows: Row[] = [
    { kind: "row", label: "Drivers used", get: (m) => `${m.driversUsed}` },
    { kind: "row", label: "Dump stops", get: (m) => `${m.dumpStops}` },
    { kind: "row", label: "Drive time (h)", get: (m) => hoursFromSeconds(m.drivingSeconds).toFixed(1) },
    { kind: "row", label: "Miles", get: (m) => milesFromMeters(m.meters).toFixed(1) },
    { kind: "row", label: "Fuel", get: (m) => money(m.cost.fuel) },
    { kind: "row", label: "Wage", get: (m) => money(m.cost.wage) },
    { kind: "row", label: "Dump fees", get: (m) => money(m.cost.dumpFees) },
    { kind: "row", label: "Operating cost", get: (m) => money(m.cost.operating), subtotal: true },
  ];

  if (hasPenalties) {
    rows.push({ kind: "section", label: "Not billed (maintenance + penalties)" });
    for (const p of shownPenalties) {
      rows.push({ kind: "row", label: p.label, get: (m) => money(p.pick(m)), indent: true });
    }
    rows.push({ kind: "row", label: "Penalties subtotal", get: (m) => money(m.cost.penalties), subtotal: true });
    rows.push({ kind: "row", label: "Grand total (incl. penalties)", get: (m) => money(m.cost.total), subtotal: true });
  }

  return (
    <table className="scorecard-table">
      <thead>
        <tr><th></th><th>Optimizer (best)</th><th>Human</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) =>
          r.kind === "section" ? (
            <tr key={`s${i}`} className="section-row"><td colSpan={3}>{r.label}</td></tr>
          ) : (
            <tr key={r.label} className={r.subtotal ? "subtotal-row" : r.indent ? "penalty-row" : ""}>
              <td>{r.label}</td>
              <td>{r.get(benchmark)}</td>
              <td>{r.get(actual)}</td>
            </tr>
          )
        )}
      </tbody>
    </table>
  );
}
